<template>
  <v-container
    fluid
    class="pa-6">
    <!-- Breadcrumb -->
    <v-breadcrumbs
      :items="breadcrumbs"
      class="pa-0 mb-2">
      <template v-slot:divider>
        <v-icon small>
          mdi-chevron-right
        </v-icon>
      </template>
      <template v-slot:item="{ item }">
        <v-breadcrumbs-item
          :disabled="item.disabled"
          :to="item.to"
          exact>
          <span :class="item.disabled ? 'grey--text' : 'primary--text'">
            {{ item.text }}
          </span>
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>

    <!-- Header -->
    <div class="d-flex align-start mb-6">
      <div>
        <div class="text-h5 font-weight-bold mb-1">
          ขั้นตอนการตรวจสอบสินค้า<template v-if="booking">
            - {{ booking.bookingNo }}
          </template>
        </div>
        <div class="body-2 grey--text">
          กรุณาตรวจสอบข้อมูลการส่ง เอกสาร และรายการสินค้าก่อนทำการอนุมัติ
        </div>
      </div>
      <v-chip
        v-if="booking"
        label
        :color="statusColor(booking.status)"
        :text-color="statusTextColor(booking.status)"
        class="font-weight-medium ml-4">
        {{ statusLabel(booking.status) }}
      </v-chip>
      <v-spacer />
      <v-btn
        v-if="booking && (booking.status === 'pending' || booking.status === 'noshow')"
        depressed
        color="secondary"
        :to="{ name: 'DeliveryBookingGRN', params: { id: $route.params.id } }"
        target="_blank">
        <v-icon
          left
          small>
          mdi-printer
        </v-icon>
        พิมพ์ใบรับสินค้า
      </v-btn>
    </div>

    <template v-if="loading">
      <v-skeleton-loader
        type="card"
        class="mb-4" />
      <v-skeleton-loader
        type="card"
        class="mb-4" />
    </template>

    <template v-else-if="booking">
      <!-- Section 1: Shipping info -->
      <v-card
        rounded="lg"
        elevation="1"
        class="mb-4">
        <div class="d-flex align-center px-5 py-4">
          <v-avatar
            size="32"
            color="secondary"
            class="mr-3">
            <span class="white--text caption font-weight-bold">1</span>
          </v-avatar>
          <v-icon
            class="mr-2"
            color="secondary">
            mdi-truck-delivery
          </v-icon>
          <span class="subtitle-1 font-weight-bold">ข้อมูลการขนส่ง</span>
        </div>
        <v-divider />
        <div class="px-5 py-4">
          <v-row>
            <v-col
              cols="12"
              sm="6">
              <div class="caption grey--text mb-1">
                วัน-เวลาที่นัดหมาย
              </div>
              <div class="body-2 font-weight-medium">
                {{ appointmentLabel }}
              </div>
            </v-col>
            <v-col
              v-if="booking.createdBy"
              cols="12"
              sm="6">
              <div class="caption grey--text mb-1">
                จองโดย
              </div>
              <div class="body-2 font-weight-medium">
                {{ booking.createdBy.name }}
              </div>
              <div class="body-2 font-weight-medium">
                {{ booking.createdBy.tel }}
              </div>
            </v-col>
            <v-col
              v-if="booking.vehicles && booking.vehicles.length"
              cols="12"
              sm="6">
              <div class="caption grey--text mb-1">
                ประเภทยานพาหนะ
              </div>
              <div
                v-for="(v, i) in booking.vehicles"
                :key="i"
                class="body-2">
                {{ v.type.th }} ×{{ v.count }}
              </div>
            </v-col>
            <v-col
              v-if="booking.packages && booking.packages.length"
              cols="12"
              sm="6">
              <div class="caption grey--text mb-1">
                ประเภทบรรจุภัณฑ์
              </div>
              <div
                v-for="(p, i) in booking.packages"
                :key="i"
                class="body-2">
                {{ p.type.th }} ×{{ p.count }}
              </div>
            </v-col>
            <v-col
              v-if="booking.deliveredAt"
              cols="12"
              sm="6">
              <div class="caption grey--text mb-1">
                วันเวลาที่จัดส่งสำเร็จ
              </div>
              <div class="body-2 font-weight-medium">
                {{ $dayjs(booking.deliveredAt.at).locale('th').format('DD-MM-YYYY | HH:mm') }} น.
              </div>
              <div
                v-if="booking.deliveredAt.by"
                class="caption grey--text">
                โดย {{ booking.deliveredAt.by.name || booking.deliveredAt.by.email }}
              </div>
            </v-col>
            <v-col
              v-if="booking.note"
              cols="12">
              <div class="caption grey--text mb-1">
                หมายเหตุ
              </div>
              <div class="body-2">
                {{ booking.note }}
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card>

      <!-- Section 2: PO List -->
      <v-card
        rounded="lg"
        elevation="1"
        class="mb-4">
        <div class="d-flex align-center px-5 py-4">
          <v-avatar
            size="32"
            color="secondary"
            class="mr-3">
            <span class="white--text caption font-weight-bold">2</span>
          </v-avatar>
          <v-icon
            class="mr-2"
            color="secondary">
            mdi-file-document-outline
          </v-icon>
          <span class="subtitle-1 font-weight-bold">เอกสารใบสั่งซื้อ (PO Documents)</span>
        </div>
        <v-divider />

        <div
          v-if="poList.length === 0"
          class="caption grey--text text-center py-6">
          ไม่มีเอกสาร
        </div>

        <v-list
          v-else
          dense>
          <div
            v-for="(po, index) in poList"
            :key="po.poOrderNo ">
            <v-list-item @click="showPo(po.poId)">
              <v-list-item-icon class="mr-3 my-auto">
                <v-icon color="secondary">
                  mdi-file-document
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="body-2 font-weight-medium">
                  {{ po.poOrderNo || `PO #${index + 1}` }} -
                  <span
                    v-if="po.isManual"
                    class="orange--text">
                    PO นอกระบบ
                  </span>
                  <span
                    v-else
                    class="blue--text">
                    PO ในระบบ
                  </span>
                </v-list-item-title>
                <v-list-item-subtitle class="caption">
                  {{ po.vendorName }}
                </v-list-item-subtitle>
                <v-list-item-subtitle class="caption">
                  <span
                    v-if="po.locationCode"
                    class="grn-po-location">
                    รับเข้า
                    <v-chip
                      x-small
                      color="green darken-3"
                      text-color="white"
                      class="ml-1 font-weight-bold">
                      {{ po.locationCode }}
                    </v-chip>
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action
                class="d-flex flex-row align-center">
                <v-chip
                  small
                  outlined>
                  {{ poUniqueSkuCount(po) }} SKUs
                </v-chip>
                <v-chip
                  class="ml-2"
                  small
                  outlined>
                  {{ poTotalQty(po).toLocaleString() }} ชิ้น
                </v-chip>
              </v-list-item-action>
            </v-list-item>
            <v-divider
              v-if="index < poList.length - 1"
              :key="`div-${index}`" />
          </div>
        </v-list>
      </v-card>

      <!-- Section 3: SKU List -->
      <v-card
        rounded="lg"
        elevation="1"
        class="mb-6">
        <div class="d-flex align-center px-5 py-4">
          <v-avatar
            size="32"
            color="secondary"
            class="mr-3">
            <span class="white--text caption font-weight-bold">3</span>
          </v-avatar>
          <v-icon
            class="mr-2"
            color="secondary">
            mdi-package-variant
          </v-icon>
          <span class="subtitle-1 font-weight-bold">ตรวจสอบรายการสินค้า ({{ poList.length }} PO)</span>
          <v-spacer />
          <span>รวมทั้งหมด</span>
          <v-chip
            class="ml-2"
            outlined
            small>
            {{ uniqueSkuCount }} SKUs
          </v-chip>
          <v-chip
            class="ml-2"
            outlined
            small>
            {{ totalDeliveryQty.toLocaleString() }} ชิ้น
          </v-chip>
        </div>
        <v-divider />

        <div
          v-if="skuLines.length === 0"
          class="caption grey--text text-center py-6">
          ไม่มีรายการสินค้า
        </div>

        <v-simple-table v-else>
          <template v-slot:default>
            <thead>
              <tr>
                <th
                  class="caption grey--text"
                  style="width:56px" />
                <th class="caption grey--text">
                  รหัสสินค้า(SKU Code)
                </th>
                <th class="caption grey--text">
                  รายละเอียดสินค้า
                </th>
                <th class="caption grey--text text-center">
                  สี / ไซส์
                </th>
                <th class="caption grey--text text-center">
                  Collection
                </th>
                <th class="caption grey--text">
                  PO
                </th>
                <th
                  class="caption grey--text text-right"
                  style="min-width:80px">
                  จำนวน
                </th>
              </tr>
            </thead>
            <tbody>
              <!-- Section label: System -->
              <tr
                v-if="systemLines.length && manualLines.length"
                class="section-label-row">
                <td
                  colspan="7"
                  class="caption grey--text font-weight-medium py-2 px-4"
                  style="background:#f5f5f5">
                  รายการจาก System
                </td>
              </tr>

              <!-- System rows -->
              <tr
                v-for="(line, i) in systemLines"
                :key="`sys-${i}`">
                <td class="py-2">
                  <v-avatar
                    size="40"
                    rounded
                    color="grey lighten-3">
                    <v-img
                      :src="line.imageUrl ? $convertImgix(`${line.imageUrl}?w=80`) : noPicture"
                      contain />
                  </v-avatar>
                </td>
                <td>
                  <span
                    class="body-2 font-weight-medium"
                    style="font-family:monospace">
                    {{ line.itemNo }}
                  </span>
                </td>
                <td class="body-2">
                  {{ line.description || '-' }}
                </td>
                <td class="caption text-center">
                  <span v-if="line.color">{{ line.color.toUpperCase() }}</span>
                  <span
                    v-if="line.color && line.size"
                    class="grey--text mx-1">/</span>
                  <span v-if="line.size">{{ line.size.toUpperCase() }}</span>
                  <span
                    v-if="!line.color && !line.size"
                    class="grey--text">-</span>
                </td>
                <td class="text-center">
                  <v-chip
                    v-if="line.gwCollection"
                    x-small
                    outlined
                    color="primary">
                    {{ line.gwCollection }}
                  </v-chip>
                  <span
                    v-else
                    class="grey--text">-</span>
                </td>
                <td class="caption">
                  {{ line.poOrderNo || '-' }}
                </td>
                <td class="body-2 text-right font-weight-medium">
                  {{ line.deliveryQty != null ? line.deliveryQty.toLocaleString() : '-' }}
                </td>
              </tr>

              <!-- Section label: Manual -->
              <tr
                v-if="manualLines.length"
                class="section-label-row">
                <td
                  colspan="7"
                  class="caption grey--text font-weight-medium py-2 px-4"
                  style="background:#f5f5f5">
                  รายการเพิ่มเอง (Manual)
                </td>
              </tr>

              <!-- Manual rows -->
              <tr
                v-for="(line, i) in manualLines"
                :key="`man-${i}`">
                <td class="py-2">
                  <v-avatar
                    size="40"
                    rounded
                    color="grey lighten-3">
                    <v-img
                      :src="noPicture"
                      contain />
                  </v-avatar>
                </td>
                <td
                  class="body-2 font-weight-medium"
                  colspan="4">
                  {{ line.name || '-' }}
                </td>
                <td class="caption grey--text">
                  {{ line.poOrderNo || '-' }}
                </td>
                <td class="body-2 text-right font-weight-medium">
                  {{ line.deliveryQty != null ? line.deliveryQty.toLocaleString() : '-' }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>

      <!-- Actions -->
      <div class="d-flex justify-end">
        <v-btn
          v-if="booking.status === 'pending' && canReject"
          depressed
          outlined
          color="error"
          class="mr-3"
          :loading="rejecting"
          @click="confirmReject = true">
          <v-icon
            left
            small>
            mdi-cancel
          </v-icon>
          ปฏิเสธการจอง (Reject)
        </v-btn>
      </div>
    </template>

    <!-- Reject dialog -->
    <v-dialog
      v-model="confirmReject"
      max-width="460">
      <v-card rounded="lg">
        <v-card-title class="subtitle-1 font-weight-bold">
          ยืนยันการปฏิเสธการจอง
        </v-card-title>
        <v-card-text>
          <div class="body-2 mb-4">
            คุณต้องการปฏิเสธการจอง
            <strong>#{{ booking && booking.bookingNo }}</strong>
            <template v-if="booking && booking.date">
              วันที่ <strong>{{ $dayjs(booking.date).locale('th').format('DD MMMM YYYY') }}</strong>
            </template>
            ใช่หรือไม่?
          </div>
          <div class="caption grey--text mb-1">
            เหตุผลในการปฏิเสธ <span class="error--text">*</span>
          </div>
          <v-textarea
            v-model="rejectReason"
            outlined
            dense
            rows="3"
            :error-messages="rejectReason.trim() === '' && rejecting ? 'กรุณาระบุเหตุผล' : ''"
            placeholder="ระบุเหตุผล..." />
          <div
            class="caption"
            style="color:#f97316">
            <v-icon
              x-small
              style="color:#f97316">
              mdi-information-outline
            </v-icon>
            เหตุผลนี้จะแสดงให้ Vendor เห็น
          </div>
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer />
          <v-btn
            text
            @click="confirmReject = false">
            ยกเลิก
          </v-btn>
          <v-btn
            depressed
            color="error"
            :loading="rejecting"
            :disabled="!rejectReason.trim()"
            @click="rejectBooking">
            ยืนยันปฏิเสธ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DeliveryBookingProvider from '@/resources/DeliveryBookingProvider'
import { mapActions } from 'vuex'
import NoPicture from '@/assets/image/no_picture_available.png'
import { sortByTimeFn } from '@/assets/js/DateTimeUtils'
import { getRole } from '@/assets/js/Authentication'
import {
  statusLabel,
  statusBg,
  statusTextColor
} from '@/assets/js/BookingStatusUtils'

const deliveryBookingService = new DeliveryBookingProvider()

export default {
  name: 'DeliveryBookingDetail',

  data () {
    return {
      booking: null,
      loading: false,
      rejecting: false,
      confirmReject: false,
      rejectReason: '',
      noPicture: NoPicture
    }
  },

  computed: {
    canReject () {
      const role = getRole()
      return !['merchandiser', 'merchandising_planner'].includes(role)
    },

    breadcrumbs () {
      return [
        { text: 'การจอง', to: { name: 'DeliveryBookingList' }, disabled: false },
        { text: this.booking ? `#${this.booking.bookingNo}` : '...', disabled: true }
      ]
    },

    appointmentLabel () {
      if (!this.booking) return '-'
      const date = this.booking.date
        ? this.$dayjs(this.booking.date).locale('th').format('DD-MM-YYYY')
        : ''
      const slots = this.booking.slots?.length
        ? sortByTimeFn(this.booking.slots, (s) => s.startTime)
        : []
      const time = slots.length
        ? `${slots[0].startTime} - ${slots[slots.length - 1].endTime} น.`
        : ''
      return [date, time].filter(Boolean).join(' | ')
    },

    poList () {
      return this.booking?.purchaseOrders || []
    },

    systemLines () {
      return this.poList
        .filter((po) => !po.isManual)
        .flatMap((po) => (po.lines || []).map((line) => ({ ...line, poOrderNo: po.poOrderNo })))
    },

    manualLines () {
      return this.poList
        .filter((po) => po.isManual)
        .flatMap((po) => (po.lines || []).map((line) => ({ ...line, poOrderNo: po.poOrderNo })))
    },

    skuLines () {
      return [...this.systemLines, ...this.manualLines]
    },

    uniqueSkuCount () {
      return new Set(this.skuLines.map((l) => l.itemNo || l.name).filter(Boolean)).size
    },

    totalDeliveryQty () {
      return this.skuLines.reduce((sum, l) => sum + (l.deliveryQty || 0), 0)
    }
  },

  async mounted () {
    await this.fetchBooking()
  },

  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),

    poUniqueSkuCount (po) {
      return new Set((po.lines || []).map((l) => l.itemNo || l.name).filter(Boolean)).size
    },

    poTotalQty (po) {
      if (!po.lines) return 0
      return po.lines.reduce((sum, line) => sum + (line.deliveryQty || 0), 0)
    },

    async fetchBooking () {
      this.loading = true
      try {
        const { data } = await deliveryBookingService.getWarehouseBookingById(this.$route.params.id)
        this.booking = data
      } catch (e) {
        this.setSnackbar({ value: true, message: 'โหลดข้อมูลไม่สำเร็จ', type: 'error' })
      } finally {
        this.loading = false
      }
    },

    async rejectBooking () {
      this.rejecting = true
      try {
        const { data } = await deliveryBookingService.rejectBooking(this.$route.params.id, this.rejectReason.trim() || undefined)
        this.booking = data
        this.setSnackbar({ value: true, message: 'ปฏิเสธการจองสำเร็จ', type: 'success' })
      } catch (e) {
        const msg = e?.response?.data?.message || 'ปฏิเสธไม่สำเร็จ'
        this.setSnackbar({ value: true, message: msg, type: 'error' })
      } finally {
        this.rejecting = false
        this.confirmReject = false
        this.rejectReason = ''
      }
    },

    statusLabel: (s) => statusLabel(s),
    statusColor: (s) => statusBg(s),
    statusTextColor: (s) => statusTextColor(s),

    showPo (poId) {
      if (!poId) return

      const routeData = this.$router.resolve({
        name: 'DeliveryNoteOfPurchaseOrder',
        params: { id: poId }
      })
      window.open(routeData.href, '_blank')
    }
  }
}
</script>
